/**
 * The FrontFlow mark: two overlapping rings, one knocked out of the other.
 *
 * Two paths, because the mark is two-tone. The solid ring takes the ink colour; the ring crossing
 * it is filled with the surface behind the mark and outlined, which is what the source draws.
 *
 * That outline is the whole of the second ring wherever the surface matches its fill, so its
 * weight is what decides whether the mark reads as two rings or as a crescent. The source draws
 * it at 2% of the mark, which is below a pixel under 50px; MARK_MIN_STROKE keeps it at a whole
 * one, which at 32px reproduces the source almost pixel for pixel on a middle scanline.
 *
 * The viewBox is cropped to the drawing, so the mark has no empty margin to centre inside. It is
 * wider than it is tall, which is what MARK_ASPECT is for: LogoMark takes a width and derives the
 * height, rather than forcing the drawing into a square.
 *
 * Converted from the VectorCraft source. Regenerate both paths together if it changes: they
 * overlap, so one redrawn on its own stops meeting the other.
 */
export const MARK_VIEWBOX = '2.16 71.86 995.68 856.28'

/** Height as a share of width, from the cropped viewBox. */
export const MARK_ASPECT = 0.86

/** The ring in the ink colour. Drawn first; the other crosses over it. */
export const MARK_SOLID_PATH =
  'M500,87.65C522.67,83.84 545.96,81.86 569.71,81.86C800.64,81.86 987.84,269.07 987.84,500C987.84,730.93 800.64,918.14 569.71,918.14C545.96,918.14 522.67,916.16 500,912.35C302.25,879.17 151.57,707.18 151.57,500C151.58,346.07 276.37,221.29 430.29,221.28C454.36,221.28 477.71,224.33 500,230.06C379.8,261.01 290.98,370.14 290.98,500C290.98,629.86 379.8,738.99 500,769.94C522.28,775.67 545.64,778.72 569.71,778.72C569.71,778.72 569.71,778.72 569.71,778.72C569.71,778.72 569.72,778.72 569.72,778.72C723.65,778.71 848.43,653.93 848.43,500C848.43,500 848.43,499.99 848.43,499.99C848.42,292.81 697.75,120.83 500,87.65Z'

/** The ring crossing it, filled with the surface behind the mark and outlined in the ink. */
export const MARK_KNOCKOUT_PATH =
  'M500,769.94C620.2,738.99 709.02,629.86 709.02,500C709.02,370.14 620.2,261.01 500,230.06C477.72,224.33 454.36,221.28 430.29,221.28C276.36,221.28 151.57,346.07 151.57,500C151.57,707.18 302.25,879.17 500,912.35C477.33,916.16 454.04,918.14 430.29,918.14C199.36,918.14 12.16,730.93 12.16,500C12.16,269.07 199.36,81.86 430.29,81.86C454.04,81.86 477.33,83.84 500,87.65C697.75,120.83 848.42,292.81 848.43,499.99C848.43,499.99 848.43,500 848.43,500C848.43,653.93 723.65,778.71 569.72,778.72C569.72,778.72 569.71,778.72 569.71,778.72C569.71,778.72 569.71,778.72 569.71,778.72C545.64,778.72 522.29,775.67 500,769.94'

// The brand scale, as tailwind.config.ts defines it. Named here because the mark is drawn in
// SVG attributes rather than classes, so it cannot reach for the tokens.
/** brand-950, the ink on a light surface. */
export const MARK_NAVY = '#0B2040'
/** The ink on a dark one, and the default surface the knocked-out ring is filled with. */
export const MARK_WHITE = '#FFFFFF'

/** The outline's share of the mark's width in the source: 20 units of 995.68. */
export const MARK_STROKE_RATIO = 0.0201
/**
 * The outline in CSS pixels, below which the second ring stops reading.
 *
 * One pixel, not more. A heavier floor was an overcorrection for a bug rather than for the
 * drawing: vectorEffect sat on the group, where it is ignored, so the stroke was being measured
 * in user units and rendered at six hundredths of a pixel. No weight made any difference until
 * that moved onto the paths, and with it fixed the drawn 2% needs only rounding up.
 */
export const MARK_MIN_STROKE = 1
